/**
 * Slice D1 — Bundle Receipt Entry clone (Salem-untouched).
 *
 * This is a LITERAL COPY of pages/Sections/SectionBundleReceiptEntry.jsx
 * with two mechanical differences:
 *   1. Endpoints point at /per-section/{slug}/bundle-receipt/...
 *      (the Phase D backend clone), instead of Salem's
 *      /consignments/section/{name}/incoming and
 *      /internal-transfer/complete-bundle-receipt.
 *   2. The component reads the slug from the URL params rather than
 *      taking it as a prop, so the same file serves color_combo,
 *      art_silk and kids without further wrapper components.
 *
 * The Salem file `pages/Sections/SectionBundleReceiptEntry.jsx` is NOT
 * imported, NOT modified, NOT deleted by this clone. Salem operators
 * will continue to use the Salem file via the Salem URL space.
 */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ConfirmRemoveBundleDialog } from '@/components/ConfirmRemoveBundleDialog';
import {
  ArrowLeft, Package, Scan, Check, X, AlertTriangle, RefreshCw, Loader2, Truck,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const SLUG_TO_DISPLAY = {
  color_combo: 'Color Combo Section',
  art_silk: 'Art Silk Section',
  kids: 'Kids Section',
};

export default function PerSectionBundleReceiptEntryClone() {
  const navigate = useNavigate();
  const { section: slug } = useParams();
  const sectionDisplayName = SLUG_TO_DISPLAY[slug] || slug;
  const backRoute = `/sections/dispatch/${slug}`;

  const [documents, setDocuments] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [scannedBundles, setScannedBundles] = useState([]);
  const [bundleInput, setBundleInput] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isScanning, setIsScanning] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const bundleInputRef = useRef(null);

  useEffect(() => { fetchDocuments(); /* eslint-disable-next-line */ }, [slug]);
  useEffect(() => {
    if (selectedDocument) {
      // Defer: selecting via the Radix <Select> restores focus to its trigger
      // on close, so land focus on the scan input on the next tick.
      setTimeout(() => bundleInputRef.current?.focus(), 100);
    }
  }, [selectedDocument]);

  const fetchDocuments = async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get(`/per-section/${slug}/bundle-receipt/incoming`);
      const docsForReceipt = (response.data?.consignments || []).filter(
        (c) => c.status === 'received' || c.status === 'accepted' || c.status === 'shipped' || c.status === 'SHIPPED',
      );
      setDocuments(docsForReceipt);
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error('Failed to load documents for receipt');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectDocument = (docId) => {
    const doc = documents.find((d) => (d.transfer_id || d.entry_id) === docId);
    setSelectedDocument(doc);
    setScannedBundles([]);
    setBundleInput('');
  };

  const handleScanBundle = async () => {
    if (!bundleInput.trim()) return;
    const barcode = bundleInput.trim().toUpperCase();
    if (scannedBundles.some((b) => b.bundle_barcode === barcode)) {
      toast.error('Bundle already scanned');
      setBundleInput('');
      return;
    }
    setIsScanning(true);
    try {
      const docBundles = selectedDocument.bundles || selectedDocument.bundle_codes || [];
      let bundleBarcodes = [];
      if (docBundles.length > 0) {
        bundleBarcodes = typeof docBundles[0] === 'string'
          ? docBundles
          : docBundles.map((b) => b.bundle_barcode || b);
      }
      const isValid = bundleBarcodes.length === 0 || bundleBarcodes.includes(barcode);
      if (!isValid) {
        toast.error(`Bundle ${barcode} is not part of this document`);
        setBundleInput('');
        setIsScanning(false);
        return;
      }
      setScannedBundles((prev) => [...prev, {
        bundle_barcode: barcode,
        scanned_at: new Date().toISOString(),
        status: 'scanned',
      }]);
      toast.success(`Bundle ${barcode} scanned`);
      setBundleInput('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to validate bundle');
    } finally {
      setIsScanning(false);
      // Defer refocus: the scan <Input> is disabled={isScanning}, so a
      // synchronous focus() before React re-enables it is a no-op.
      setTimeout(() => bundleInputRef.current?.focus(), 50);
    }
  };

  const [removeBundleTarget, setRemoveBundleTarget] = useState(null);

  const handleRemoveBundle = (barcode) => {
    setScannedBundles((prev) => prev.filter((b) => b.bundle_barcode !== barcode));
  };

  const handleSubmitReceipt = async () => {
    if (scannedBundles.length === 0) {
      toast.error('Please scan at least one bundle');
      return;
    }
    // Enforce full receipt — must scan ALL expected bundles before submission.
    const expectedTotal = selectedDocument?.bundle_count
      || selectedDocument?.bundles?.length
      || selectedDocument?.bundle_codes?.length
      || 0;
    if (expectedTotal > 0 && scannedBundles.length < expectedTotal) {
      const remaining = expectedTotal - scannedBundles.length;
      toast.error(
        `Cannot submit: ${remaining} of ${expectedTotal} bundle(s) not yet scanned. ` +
        `Please scan all bundles before completing receipt.`,
      );
      return;
    }
    setIsSubmitting(true);
    try {
      const docId = selectedDocument.transfer_id || selectedDocument.entry_id;
      await apiClient.post(`/per-section/${slug}/bundle-receipt/complete`, {
        transfer_id: docId,
        bundles: scannedBundles.map((b) => b.bundle_barcode),
      });
      toast.success(`Bundle receipt completed for ${scannedBundles.length} bundles`);
      setSelectedDocument(null);
      setScannedBundles([]);
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete bundle receipt');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const cfg = {
      received:  { color: 'bg-green-100 text-green-800',  label: 'Received' },
      accepted:  { color: 'bg-blue-100 text-blue-800',    label: 'Accepted' },
      shipped:   { color: 'bg-orange-100 text-orange-800', label: 'Shipped' },
      SHIPPED:   { color: 'bg-orange-100 text-orange-800', label: 'Shipped' },
      REQUESTED: { color: 'bg-purple-100 text-purple-800', label: 'Requested' },
      READY:     { color: 'bg-amber-100 text-amber-800',  label: 'Ready' },
    }[status] || { color: 'bg-gray-100 text-gray-800', label: status };
    return <Badge className={cfg.color}>{cfg.label}</Badge>;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#FFFBF5] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#2D5F4F]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid={`per-section-bundle-receipt-${slug}`}>
      <header className="bg-[#1A1F2E] text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(backRoute)}
              className="text-white hover:bg-white/10"
              data-testid="back-button"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold">Bundle Receipt Entry</h1>
              <p className="text-sm text-gray-400">{sectionDisplayName}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={fetchDocuments}
            className="text-white hover:bg-white/10"
            data-testid="refresh-button"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Document Selection */}
          <Card className="bg-white shadow-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Truck className="w-5 h-5" />
                Select Document
              </CardTitle>
              <CardDescription>
                Choose a received document to start bundle receipt
              </CardDescription>
            </CardHeader>
            <CardContent>
              {documents.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Package className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <p>No documents ready for bundle receipt</p>
                  <p className="text-sm mt-2">Accept incoming consignments first</p>
                </div>
              ) : (
                <Select
                  value={selectedDocument?.transfer_id || selectedDocument?.entry_id || ''}
                  onValueChange={handleSelectDocument}
                >
                  <SelectTrigger data-testid="document-select">
                    <SelectValue placeholder="Select a document..." />
                  </SelectTrigger>
                  <SelectContent>
                    {documents.map((doc) => {
                      const docId = doc.transfer_id || doc.entry_id;
                      const bundleCount = doc.bundle_count || doc.bundles?.length || doc.bundle_codes?.length || 0;
                      return (
                        <SelectItem key={docId} value={docId}>
                          {docId} - {doc.from_section || doc.vendor_name} ({bundleCount} bundles)
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              )}

              {selectedDocument && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg" data-testid="selected-document-card">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium">{selectedDocument.transfer_id || selectedDocument.entry_id}</span>
                    {getStatusBadge(selectedDocument.status)}
                  </div>
                  <div className="text-sm text-gray-600 space-y-1">
                    <p>From: {selectedDocument.from_section || selectedDocument.vendor_name}</p>
                    <p>Bundles: {selectedDocument.bundle_count || selectedDocument.bundles?.length || selectedDocument.bundle_codes?.length || 'N/A'}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Bundle Scanning */}
          <Card className="bg-white shadow-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Scan className="w-5 h-5" />
                Scan Bundles
              </CardTitle>
              <CardDescription>
                Scan bundle barcodes to record receipt
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!selectedDocument ? (
                <div className="text-center py-8 text-gray-500">
                  <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <p>Select a document first</p>
                </div>
              ) : (
                <>
                  <div className="flex gap-2 mb-2">
                    <Input
                      ref={bundleInputRef}
                      value={bundleInput}
                      onChange={(e) => setBundleInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleScanBundle()}
                      placeholder="Scan or enter bundle barcode"
                      disabled={isScanning}
                      data-testid="bundle-input"
                    />
                    <Button
                      onClick={handleScanBundle}
                      disabled={isScanning || !bundleInput.trim()}
                      data-testid="scan-btn"
                    >
                      {isScanning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Scan className="w-4 h-4" />}
                    </Button>
                  </div>
                  <div className="mb-4 text-sm font-medium text-[#2D5F4F]" data-testid="scanned-bundle-count">
                    {scannedBundles.length} bundle{scannedBundles.length !== 1 ? 's' : ''} scanned
                    {(() => {
                      const total = selectedDocument?.bundle_count || selectedDocument?.bundles?.length || selectedDocument?.bundle_codes?.length || 0;
                      return total > 0 ? ` / ${total} total` : '';
                    })()}
                  </div>

                  {scannedBundles.length > 0 && (
                    <div className="border rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Bundle Barcode</TableHead>
                            <TableHead>Scanned At</TableHead>
                            <TableHead className="w-16"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {scannedBundles.map((bundle) => (
                            <TableRow key={bundle.bundle_barcode}>
                              <TableCell className="font-mono">{bundle.bundle_barcode}</TableCell>
                              <TableCell>{new Date(bundle.scanned_at).toLocaleTimeString()}</TableCell>
                              <TableCell>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setRemoveBundleTarget(bundle.bundle_barcode)}
                                  className="text-red-600 hover:text-red-700"
                                  data-testid={`remove-bundle-${bundle.bundle_barcode}`}
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}

                  <div className="mt-4 flex justify-between items-center">
                    {(() => {
                      const expectedTotal = selectedDocument?.bundle_count
                        || selectedDocument?.bundles?.length
                        || selectedDocument?.bundle_codes?.length
                        || 0;
                      const isComplete = expectedTotal === 0 || scannedBundles.length >= expectedTotal;
                      const remaining = Math.max(expectedTotal - scannedBundles.length, 0);
                      return (
                        <>
                          <span
                            className={`text-sm ${isComplete ? 'text-gray-600' : 'text-amber-700 font-medium'}`}
                            data-testid="receipt-progress-text"
                          >
                            {expectedTotal > 0
                              ? (isComplete
                                  ? `All ${expectedTotal} bundles scanned`
                                  : `${remaining} of ${expectedTotal} bundle(s) remaining`)
                              : `Scanned: ${scannedBundles.length} bundles`}
                          </span>
                          <Button
                            onClick={handleSubmitReceipt}
                            disabled={isSubmitting || scannedBundles.length === 0 || !isComplete}
                            className="bg-[#2D5F4F] hover:bg-[#234A3D]"
                            data-testid="submit-receipt-btn"
                          >
                            {isSubmitting ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Submitting...
                              </>
                            ) : (
                              <>
                                <Check className="w-4 h-4 mr-2" />
                                Complete Receipt
                              </>
                            )}
                          </Button>
                        </>
                      );
                    })()}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
      <ConfirmRemoveBundleDialog
        barcode={removeBundleTarget}
        onConfirm={() => { handleRemoveBundle(removeBundleTarget); setRemoveBundleTarget(null); }}
        onCancel={() => setRemoveBundleTarget(null)}
      />
    </div>
  );
}
